import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Recycle, Trash, CircleNotch, CaretUpDown, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Toaster, toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Live server-side search combobox for picking an Output Item (Sep 29
// 2026) - backed by /api/products/search (component_master, 3200+ known
// parts) since the full product list is too large to load client-side
// like the Cost Center page's stock-based combobox does.
const OutputItemSearch = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const handleQueryChange = (value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) { setResults([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/products/search`, { params: { q: value.trim(), limit: 15 } });
        setResults(data || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-9 w-full flex items-center justify-between px-3 rounded-md border border-[#D0D5DD] bg-white text-sm text-left"
          data-testid="byproduct-exemption-item-search-trigger"
        >
          <span className="text-[#98A2B3] flex items-center gap-2"><MagnifyingGlass size={14} /> Search Product ID or description...</span>
          <CaretUpDown size={13} className="text-[#667085] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={handleQueryChange} placeholder="Type at least 1 character..." data-testid="byproduct-exemption-item-search-input" />
          <CommandList>
            {loading && <div className="py-3 text-center text-xs text-[#667085]">Searching...</div>}
            {!loading && query.trim() && results.length === 0 && <CommandEmpty>No matching product found.</CommandEmpty>}
            <CommandGroup>
              {results.map((r) => (
                <CommandItem
                  key={r.product_id}
                  value={r.product_id}
                  onSelect={() => { onSelect(r); setOpen(false); setQuery(""); setResults([]); }}
                  data-testid={`byproduct-exemption-item-option-${r.product_id}`}
                >
                  <span className="font-mono text-xs font-bold text-[#101828] mr-2">{r.product_id}</span>
                  <span className="text-xs text-[#667085]">{r.description || "—"}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default function ByproductExemptionPage() {
  const [exemptions, setExemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const loadExemptions = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/byproduct-exemptions`);
      setExemptions(data.exemptions || []);
    } catch (e) {
      toast.error("Failed to load exemptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadExemptions(); }, []);

  const handleAdd = async () => {
    if (!selected) { toast.error("Search and select an output item first"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/byproduct-exemptions`, { product_id: selected.product_id, description: selected.description });
      toast.success(`${selected.product_id} exempted from the AI by-product suggestion`);
      setSelected(null);
      loadExemptions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add exemption");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (productId) => {
    setRemovingId(productId);
    try {
      await axios.delete(`${API}/byproduct-exemptions/${encodeURIComponent(productId)}`);
      toast.success(`${productId} removed from exemptions`);
      loadExemptions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove exemption");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col" data-testid="byproduct-exemption-page">
      <Toaster position="top-right" />
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Recycle size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Byproduct Exemption</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="p-4 sm:p-6 max-w-3xl w-full mx-auto">
        <div className="flex items-center gap-2 mb-1.5">
          <Recycle size={20} weight="fill" className="text-[#0E7C86]" />
          <h1 className="font-heading text-lg font-bold text-[#101828]">Byproduct Exemption</h1>
        </div>
        <p className="text-sm text-[#667085] mb-5">
          Output items listed here skip the AI by-product suggestion entirely on the Confirm Task screen - no scrap/by-product
          block is shown and confirmation is never blocked waiting on Net Weight data for these items.
        </p>

        <div className="bg-white border border-[#E4E7EC] rounded-xl p-4 sm:p-5 space-y-3 mb-6">
          <Label className="text-xs font-bold text-[#344054]">Output Item</Label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              {selected ? (
                <div className="h-9 flex items-center px-3 rounded-md border border-[#0E7C86] bg-[#ECFDF3] text-sm" data-testid="byproduct-exemption-item-selected">
                  <span className="font-mono font-bold text-[#101828] mr-2">{selected.product_id}</span>
                  <span className="text-[#667085] truncate">{selected.description}</span>
                  <button onClick={() => setSelected(null)} className="ml-auto text-[#667085] hover:text-[#B42318]" data-testid="byproduct-exemption-item-clear">✕</button>
                </div>
              ) : (
                <OutputItemSearch onSelect={setSelected} />
              )}
            </div>
            <Button onClick={handleAdd} disabled={!selected || saving} className="bg-[#0E7C86] hover:bg-[#0B6B74] shrink-0" data-testid="byproduct-exemption-add-btn">
              {saving ? <CircleNotch size={14} className="animate-spin" /> : "Add Exemption"}
            </Button>
          </div>
        </div>

        <Label className="text-xs font-bold text-[#344054] mb-2 block">Currently Exempted Items</Label>
        {loading ? (
          <p className="text-sm text-[#667085]" data-testid="byproduct-exemption-loading">Loading...</p>
        ) : exemptions.length === 0 ? (
          <p className="text-sm text-[#667085]" data-testid="byproduct-exemption-empty">No items exempted yet.</p>
        ) : (
          <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9FAFB]">
                  <TableHead className="text-xs">Product ID</TableHead>
                  <TableHead className="text-xs">Description</TableHead>
                  <TableHead className="text-xs">Exempted By</TableHead>
                  <TableHead className="text-xs w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exemptions.map((e) => (
                  <TableRow key={e.product_id} data-testid={`byproduct-exemption-row-${e.product_id}`}>
                    <TableCell className="font-mono text-sm font-bold text-[#101828]">{e.product_id}</TableCell>
                    <TableCell className="text-xs text-[#667085]">{e.description || "—"}</TableCell>
                    <TableCell className="text-xs text-[#667085]">{e.exempted_by || "—"}</TableCell>
                    <TableCell>
                      <button onClick={() => handleRemove(e.product_id)} disabled={removingId === e.product_id} className="text-[#B42318] disabled:opacity-40" data-testid={`byproduct-exemption-remove-${e.product_id}`}>
                        {removingId === e.product_id ? <CircleNotch size={14} className="animate-spin" /> : <Trash size={14} weight="bold" />}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
